import { Box, createStyles, makeStyles } from '@material-ui/core'
import React, { useMemo } from 'react'
import clsx from 'clsx'
import { RichText as RichTextType } from 'types/post'
import backgroundAlpha from './../../../../portal/src/assets/images/hero_alpha.png'
import RichText from '../RichText'
import { RBBVariants } from '../../types/landingPageV2'
import { AssetFluid } from 'types/asset'
import loadable from '@loadable/component'

interface Props {
  variant: RBBVariants
  content: RichTextType
  bottom: RichTextType
  images: AssetFluid[]
  sectionClass: string
}

const Image = loadable(() => import('../ContentfulEntries/Image'))

function RoundBackgroundBlock(props: Props) {
  const { content, variant, images, sectionClass, bottom } = props

  const classes = useStyles()

  const Content = useMemo(() => {
    switch (variant) {
      case RBBVariants.HearFromCustomers:
        const imgCoords = [
          { top: '30%', left: '6rem' },
          { top: '65%', left: '12rem' },
          { top: '20%', right: '6rem' },
        ]
      default:
        return (
          <>
            {images.map((image, index) => (
              <div
                key={image.contentful_id}
                style={imgCoords[index]}
                className={clsx(classes.img)}
              >
                <Image asset={image} />
              </div>
            ))}
            <Box className={classes.centerColumn}>
              <RichText rawBody={content.raw} references={content.references} />
            </Box>
          </>
        )
    }
  }, [variant, images])

  const Bottom = useMemo(() => {
    if (bottom) {
      return <RichText rawBody={bottom.raw} references={bottom.references} />
    }
    return null
  }, [bottom])

  return (
    <Box className={clsx(sectionClass, classes.paddingVertical)}>
      <Box
        className={clsx({
          [classes.innerContainer]: true,
        })}
      >
        <Box className={classes.bgImgLeft} />
        <Box className={classes.bgImgRight} />
        {Content}
      </Box>
      <Box
        className={clsx({
          [classes.bottom]: true,
          [classes.bottomHearFromCustomers]:
            variant === RBBVariants.HearFromCustomers,
        })}
      >
        {Bottom}
      </Box>
    </Box>
  )
}

const useStyles = makeStyles((theme) =>
  createStyles({
    bgImgLeft: {
      position: 'absolute',
      left: -150,
      bottom: 60,
      transform: 'rotate(-6deg)',
      width: 890,
      height: 961,
      background: `center center / 100% no-repeat url(${backgroundAlpha}) ${theme.colors.blue[10]}`,
      zIndex: 0,
    },
    bgImgRight: {
      position: 'absolute',
      right: -400,
      bottom: -200,
      transform: 'rotate(26deg)',
      width: 728,
      height: 786,
      background: `center center / 100% no-repeat url(${backgroundAlpha}) ${theme.colors.blue[10]}`,
      zIndex: 0,
    },
    bottom: {
      marginTop: 32,
      '& p': {
        color: theme.colors.neutral[50],
      },
      '& h1': {
        fontWeight: 400,
        color: theme.colors.neutral[90],
      },
    },
    bottomHearFromCustomers: {
      '& > p:last-child': {
        fontSize: 14,
        textAlign: 'center',
        marginTop: 32,
      },
    },
    img: {
      position: 'absolute',
      [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        display: 'none',
      },
    },
    centerColumn: {
      zIndex: 2,
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 15rem',
      textAlign: 'center',
      '& > h2': {
        fontSize: '43px !important',
        fontWeight: '400 !important',
      },
      [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        padding: 0,
      },
    },
    paddingVertical: {
      paddingTop: '4rem',
      paddingBottom: '4rem',
    },
    innerContainer: {
      overflow: 'hidden',
      position: 'relative',
      borderRadius: '24px',
      background: theme.colors.blue[10],
      backgroundBlendMode: 'multiply',
      display: 'flex',
      flex: 1,
      gap: '4rem',
      padding: '120px 80px',
      [theme.breakpoints.down(theme.breakpoints.values.tablet)]: {
        padding: '40px 24px',
        borderRadius: '20px',
      },
    },
    container: {
      alignItems: 'center',
      flexDirection: 'row',
      width: '100%',
    },
  })
)

export default RoundBackgroundBlock
